import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Space,
  message,
  Radio,
  Checkbox,
  Divider,
  Typography,
  Spin,
  Alert,
  Modal
} from 'antd';
import { SaveOutlined, DeleteOutlined, PlusOutlined, CheckCircleOutlined, CheckCircleOutlined as CheckOutlined } from '@ant-design/icons';
import { aiSettingsService } from '../services/aiSettingsService';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AISettingsForm = ({ organizationId, onSave }) => {
  const [form] = Form.useForm();
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiSettings, setAiSettings] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [campaignForm] = Form.useForm();
  const [showCampaignForm, setShowCampaignForm] = useState(false);

  useEffect(() => {
    fetchProviders();
    fetchAiSettings();
  }, [organizationId]);

  const fetchProviders = async () => {
    try {
      const response = await aiSettingsService.getProviders();
      if (response.success) {
        setProviders(response.data);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      message.error('Failed to fetch AI providers');
    }
  };

  const fetchAiSettings = async () => {
    setLoading(true);
    try {
      const response = await aiSettingsService.getAiSettings(organizationId);
      if (response.success) {
        setAiSettings(response.data);
        setCampaigns(response.data.campaigns || []);
        if (response.data.provider) {
          setSelectedProvider(response.data.provider);
          form.setFieldsValue({
            provider: response.data.provider,
            model: response.data.model,
            apiKey: response.data.apiKey || ''
          });
          // Fetch models for the provider
          fetchModels(response.data.provider);
        }
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error);
      message.error('Failed to fetch AI settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async (provider) => {
    try {
      const response = await aiSettingsService.getModels(provider);
      if (response.success) {
        setModels(response.data);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      message.error('Failed to fetch models');
    }
  };

  const handleProviderChange = (value) => {
    setSelectedProvider(value);
    form.setFieldValue('model', null);
    fetchModels(value);
  };

  const handleSaveProviderSettings = async (values) => {
    try {
      setLoading(true);
      const response = await aiSettingsService.updateProviderSettings(organizationId, values);
      if (response.success) {
        message.success('AI provider settings saved successfully');
        setAiSettings(response.data);
        if (onSave) {
          onSave();
        }
      }
    } catch (error) {
      console.error('Error saving provider settings:', error);
      message.error('Failed to save provider settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCampaign = async (values) => {
    try {
      setLoading(true);
      let response;
      if (editingCampaignId) {
        response = await aiSettingsService.updateCampaign(organizationId, editingCampaignId, values);
      } else {
        response = await aiSettingsService.createCampaign(organizationId, values);
      }
      if (response.success) {
        message.success(editingCampaignId ? 'Campaign updated successfully' : 'Campaign created successfully');
        setAiSettings(response.data);
        setCampaigns(response.data.campaigns || []);
        campaignForm.resetFields();
        setShowCampaignForm(false);
        setEditingCampaignId(null);
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      message.error('Failed to save campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaignId(campaign._id);
    campaignForm.setFieldsValue({
      campaignTitle: campaign.campaignTitle,
      initialMessage: campaign.initialMessage,
      productServiceName: campaign.productServiceName,
      description: campaign.description,
      keyFeatures: campaign.keyFeatures,
      pricingInformation: campaign.pricingInformation,
      primaryCallObjective: campaign.primaryCallObjective,
      bookCalendar: campaign.bookCalendar,
      successActionPlan: campaign.successActionPlan,
      aiGuidelinesRestrictions: campaign.aiGuidelinesRestrictions,
      disconnectAction: campaign.disconnectAction,
      scheduleType: campaign.scheduleType
    });
    setShowCampaignForm(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      setLoading(true);
      const response = await aiSettingsService.deleteCampaign(organizationId, campaignId);
      if (response.success) {
        message.success('Campaign deleted successfully');
        setAiSettings(response.data);
        setCampaigns(response.data.campaigns || []);
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      message.error('Failed to delete campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCampaignForm = () => {
    setShowCampaignForm(false);
    setEditingCampaignId(null);
    campaignForm.resetFields();
  };

  const handleSetActiveCampaign = async (campaignId) => {
    try {
      setLoading(true);
      const response = await aiSettingsService.setActiveCampaign(organizationId, campaignId);
      if (response.success) {
        message.success('Campaign set as active');
        setAiSettings(response.data);
        setCampaigns(response.data.campaigns || []);
      }
    } catch (error) {
      console.error('Error setting active campaign:', error);
      message.error('Failed to set active campaign');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !aiSettings) {
    return <Spin size="large" />;
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Provider Settings Section */}
      <Card title="AI Provider Settings" style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveProviderSettings}
          loading={loading}
        >
          <Row gutter={24}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="AI Provider"
                name="provider"
                rules={[{ required: true, message: 'Please select an AI provider' }]}
              >
                <Select
                  placeholder="Select AI Provider"
                  onChange={handleProviderChange}
                  options={providers.map(p => ({
                    label: p.provider,
                    value: p.provider
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Model"
                name="model"
                rules={[{ required: true, message: 'Please select a model' }]}
              >
                <Select
                  placeholder="Select Model"
                  disabled={!selectedProvider}
                  options={models.map(m => ({
                    label: m,
                    value: m
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="API Key"
            name="apiKey"
            rules={[{ required: true, message: 'Please enter API key' }]}
          >
            <Input.Password placeholder="Enter your API key" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
              Save Provider Settings
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Campaigns Section */}
      <Card title="AI Calling Campaigns" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          {!showCampaignForm && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowCampaignForm(true)}
            >
              Create New Campaign
            </Button>
          )}
        </div>

        <Modal
          title={editingCampaignId ? 'Edit Campaign' : 'Create New Campaign'}
          open={showCampaignForm}
          onCancel={handleCancelCampaignForm}
          footer={null}
          width={800}
          bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
        >
          <Form
            form={campaignForm}
            layout="vertical"
            onFinish={handleSaveCampaign}
          >
            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Campaign Title"
                  name="campaignTitle"
                  rules={[{ required: true, message: 'Campaign title is required' }]}
                >
                  <Input placeholder="Enter campaign title" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Product/Service Name"
                  name="productServiceName"
                  rules={[{ required: true, message: 'Product/Service name is required' }]}
                >
                  <Input placeholder="Enter product or service name" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Initial Message"
              name="initialMessage"
              rules={[{ required: true, message: 'Initial message is required' }]}
            >
              <TextArea rows={3} placeholder="Enter the initial message for the call" />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: 'Description is required' }]}
            >
              <TextArea rows={3} placeholder="Describe the campaign" />
            </Form.Item>

            <Form.Item
              label="Key Features"
              name="keyFeatures"
              rules={[{ required: true, message: 'Key features are required' }]}
            >
              <TextArea rows={3} placeholder="List key features (comma-separated or line-separated)" />
            </Form.Item>

            <Form.Item
              label="Pricing Information"
              name="pricingInformation"
            >
              <TextArea rows={2} placeholder="Enter pricing information (optional)" />
            </Form.Item>

            <Form.Item
              label="Primary Call Objective"
              name="primaryCallObjective"
              rules={[{ required: true, message: 'Primary call objective is required' }]}
            >
              <TextArea rows={2} placeholder="What is the main objective of the call?" />
            </Form.Item>

            <Form.Item
              label="Success Action Plan"
              name="successActionPlan"
              rules={[{ required: true, message: 'Success action plan is required' }]}
            >
              <TextArea rows={3} placeholder="What should happen when the call is successful?" />
            </Form.Item>

            <Form.Item
              label="AI Guidelines & Restrictions"
              name="aiGuidelinesRestrictions"
              rules={[{ required: true, message: 'AI guidelines are required' }]}
            >
              <TextArea rows={3} placeholder="Define guidelines and restrictions for the AI" />
            </Form.Item>

            <Form.Item
              label="Disconnect Action"
              name="disconnectAction"
              rules={[{ required: true, message: 'Disconnect action is required' }]}
            >
              <TextArea rows={2} placeholder="What should happen when the call is disconnected?" />
            </Form.Item>

            <Row gutter={24}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Schedule Type"
                  name="scheduleType"
                  rules={[{ required: true, message: 'Schedule type is required' }]}
                >
                  <Radio.Group>
                    <Radio value="immediate">Immediate</Radio>
                    <Radio value="schedule_later">Schedule Later</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Book Calendar"
                  name="bookCalendar"
                  valuePropName="checked"
                >
                  <Checkbox>Enable calendar booking</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {editingCampaignId ? 'Update Campaign' : 'Create Campaign'}
                </Button>
                <Button onClick={handleCancelCampaignForm}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Campaigns List */}
        {campaigns.length > 0 ? (
          <div>
            <Divider />
            <Title level={5}>Active Campaigns ({campaigns.length})</Title>
            {campaigns.map(campaign => (
              <Card
                key={campaign._id}
                style={{ marginBottom: 16 }}
                title={campaign.campaignTitle}
                extra={
                  <Space>
                    <Button
                      type={campaign.isActive ? 'primary' : 'default'}
                      size="small"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleSetActiveCampaign(campaign._id)}
                    >
                      {campaign.isActive ? 'Active' : 'Set Active'}
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleEditCampaign(campaign)}
                    >
                      Edit
                    </Button>
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteCampaign(campaign._id)}
                    >
                      Delete
                    </Button>
                  </Space>
                }
              >
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Product/Service:</Text>
                      <div>{campaign.productServiceName}</div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Schedule Type:</Text>
                      <div>{campaign.scheduleType === 'immediate' ? 'Immediate' : 'Schedule Later'}</div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Book Calendar:</Text>
                      <div>{campaign.bookCalendar ? 'Yes' : 'No'}</div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>Created:</Text>
                      <div>{new Date(campaign.createdAt).toLocaleDateString()}</div>
                    </div>
                  </Col>
                </Row>
                <Divider />
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Text strong>Description:</Text>
                    <div style={{ marginBottom: 12 }}>{campaign.description}</div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Text strong>Primary Call Objective:</Text>
                    <div>{campaign.primaryCallObjective}</div>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        ) : (
          <Alert
            message="No campaigns created yet"
            description="Create your first AI calling campaign to get started"
            type="info"
            showIcon
          />
        )}
      </Card>
    </div>
  );
};

export default AISettingsForm;
